import React, { useState, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import {
    BsSpeedometer2, BsSearch, BsCheckAll, BsSave,
    BsSortAlphaDown, BsSortAlphaUp, BsFileEarmarkPdf,
    BsCheckLg, BsExclamationTriangleFill
} from 'react-icons/bs';
import { FaTimes, FaSlidersH, FaCheckCircle, FaWeight, FaUserNinja } from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { AppContext } from '../../context/AppContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const AdminWeighIn = ({ type: propType }) => {
    const { type: paramType } = useParams();
    const activeType = propType || paramType || 'Prestasi';
    const currentType = activeType.charAt(0).toUpperCase() + activeType.slice(1);
    const isFestival = currentType === 'Festival';

    const { masterData, settings } = useContext(AppContext);

    // Tahun event untuk kalkulasi umur yang tepat
    const eventYear = settings?.event_year || new Date().getFullYear();

    const [athletes, setAthletes] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);

    // State Input Inline
    const [inputData, setInputData] = useState({});

    // Filters & Setting
    const [tolerance, setTolerance] = useState(0.5);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ division: '', gender: '', team_id: '', weight_class: '' });
    const [sortBy, setSortBy] = useState('team');
    const [sortOrder, setSortOrder] = useState('asc');

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // Modals
    const [showToleranceModal, setShowToleranceModal] = useState(false);
    const [tempTolerance, setTempTolerance] = useState(tolerance);
    const [showPassAllModal, setShowPassAllModal] = useState(false);
    const [tempPassAllTeam, setTempPassAllTeam] = useState('');
    const [isPassingAll, setIsPassingAll] = useState(false);

    // --- FETCH DATA ---
    const fetchAthletes = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit,
                search: searchTerm,
                type: currentType,
                division: filters.division,
                gender: filters.gender,
                team_id: filters.team_id,
                weight_class: filters.weight_class, // Sekarang nilainya sudah format U/O (contoh: U45)
                competition_type: 'Kyorugi' // Wajib untuk memastikan Poomsae tidak masuk
            };

            const response = await api.get('/weighin', { params });
            const fetchedData = response.data.data || response.data.result || [];

            setAthletes(fetchedData);
            setTotalPages(response.data.totalPages || response.data.totalPage || 0);
            setTotalItems(response.data.totalItems || response.data.totalRows || 0);

            // Inisialisasi State Input Inline
            const initialInputs = {};
            fetchedData.forEach(a => {
                initialInputs[a.uuid || a.id] = {
                    actual_weight: a.actual_weight || '',
                    height: a.height || ''
                };
            });
            setInputData(initialInputs);
        } catch (error) {
            toast.error("Gagal mengambil data atlet.");
        } finally {
            setLoading(false);
        }
    };

    const fetchTeams = async () => {
        try {
            const res = await api.get('/teams', { params: { limit: 1000 } });
            setTeams(res.data.result || res.data || []);
        } catch (error) {
            console.error("Gagal fetch tim");
        }
    };

    useEffect(() => {
        fetchTeams();
    }, []);

    useEffect(() => {
        const delay = setTimeout(() => fetchAthletes(), 500);
        return () => clearTimeout(delay);
        // eslint-disable-next-line
    }, [page, limit, searchTerm, filters, currentType]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, filters.division, filters.gender, filters.team_id, filters.weight_class, currentType]);

    // Reset filter kelas & gender HANYA tepat saat berpindah ke tab Festival
    useEffect(() => {
        console.log("Tab switched. Is Festival:", isFestival);
        if (isFestival) {
            setFilters(prev => ({ ...prev, gender: '', weight_class: '' }));
        }
    }, [isFestival]);

    // --- FUNGSI NORMALISASI KELAS ("Under 45" -> "U45") ---
    const normalizeClass = (val) => {
        if (!val) return '';
        let str = val.toString().trim();
        let upper = str.toUpperCase();

        // Ubah format Master Data "Under X" menjadi format DB "UX"
        if (upper.includes('UNDER')) return 'U' + str.replace(/[^\d.]/g, '');
        // Ubah format Master Data "Over X" menjadi format DB "OX"
        if (upper.includes('OVER')) return 'O' + str.replace(/[^\d.]/g, '');

        return str;
    };

    // --- SMART DROPDOWN KELAS ---
    const availableClasses = useMemo(() => {
        if (!masterData?.weight_classes) return [];
        let classes = new Set();

        Object.keys(masterData.weight_classes).forEach(div => {
            if (filters.division && filters.division.toLowerCase() !== div.toLowerCase()) return;

            const genders = masterData.weight_classes[div];
            if (genders) {
                Object.keys(genders).forEach(gen => {
                    let mappedGenFilter = filters.gender;
                    if (filters.gender === 'Lelaki') mappedGenFilter = 'Putra';
                    if (filters.gender === 'Perempuan') mappedGenFilter = 'Putri';

                    if (filters.gender && mappedGenFilter.toLowerCase() !== gen.toLowerCase() && filters.gender.toLowerCase() !== gen.toLowerCase()) return;

                    if (Array.isArray(genders[gen])) {
                        // Di sini kita normalisasi langsung nilainya sebelum masuk ke array
                        genders[gen].forEach(c => classes.add(normalizeClass(c)));
                    }
                });
            }
        });

        return Array.from(classes).sort((a, b) => {
            const numA = parseFloat(a.replace(/[^\d.]/g, '')) || 0;
            const numB = parseFloat(b.replace(/[^\d.]/g, '')) || 0;
            return numA - numB;
        });
    }, [masterData, filters.division, filters.gender]);

    const sortedAthletes = useMemo(() => {
        let sortable = [...athletes];
        sortable.sort((a, b) => {
            let valA = sortBy === 'team' ? (a.team?.name || '') : a.name;
            let valB = sortBy === 'team' ? (b.team?.name || '') : b.name;
            valA = valA.toLowerCase(); valB = valB.toLowerCase();
            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        return sortable;
    }, [athletes, sortBy, sortOrder]);

    const toggleSort = (column) => {
        if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        else { setSortBy(column); setSortOrder('asc'); }
    };

    // --- LOGIKA LIVE EVALUATION & AUTO-SAVE ---
    const parseWeightClass = (weightClassStr) => {
        if (!weightClassStr) return { min: 0, max: 999 };
        const cleanStr = weightClassStr.replace(/[^0-9OoverUnder\-.]/gi, '').trim();

        if (cleanStr.toLowerCase().includes('under') || cleanStr.startsWith('U')) {
            const max = parseFloat(cleanStr.replace(/[^0-9.]/g, ''));
            return { min: 0, max: max };
        }
        if (cleanStr.toLowerCase().includes('over') || cleanStr.startsWith('O')) {
            const min = parseFloat(cleanStr.replace(/[^0-9.]/g, ''));
            return { min: min, max: 999 };
        }

        const parts = cleanStr.split('-');
        if (parts.length === 2) return { min: parseFloat(parts[0]), max: parseFloat(parts[1]) };
        return { min: 0, max: 999 };
    };

    const evaluateWeightStatus = (athlete, actualWeightVal) => {
        if (!actualWeightVal) return athlete.weigh_in_status || 'Pending';
        if (isFestival) return 'Pass';

        const weight = parseFloat(actualWeightVal);
        if (isNaN(weight)) return athlete.weigh_in_status || 'Pending';

        const { min, max } = parseWeightClass(athlete.weight_class);
        if (weight >= (min - tolerance) && weight <= (max + tolerance)) {
            return 'Pass';
        } else {
            return 'Fail';
        }
    };

    const handleInputChange = (id, field, value) => {
        setInputData(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    const handleSaveWeight = async (athlete, isAutoSave = false) => {
        const id = athlete.uuid || athlete.id;
        const actualWeight = inputData[id]?.actual_weight;
        const actualHeight = inputData[id]?.height;

        if (!actualWeight && !isFestival) return;

        const statusToSave = evaluateWeightStatus(athlete, actualWeight);

        if (!isFestival && statusToSave === 'Fail' && !isAutoSave) {
            const { min, max } = parseWeightClass(athlete.weight_class);
            if (!window.confirm(`Berat di luar toleransi (${Math.max(0, min - tolerance).toFixed(1)} - ${(max + tolerance).toFixed(1)}kg). Tandai GAGAL?`)) return;
        }

        try {
            await api.patch('/weighin/update', {
                id: id,
                actual_weight: actualWeight,
                height: actualHeight || null,
                weigh_in_status: statusToSave
            });

            setAthletes(prev => prev.map(a => (a.uuid || a.id) === id ? { ...a, weigh_in_status: statusToSave, actual_weight: actualWeight, height: actualHeight } : a));

            if (!isAutoSave) {
                toast.success(`Status ${athlete.name} disimpan: ${statusToSave === 'Pass' ? 'Lolos' : 'Gagal'}`);
            }
        } catch (error) {
            if (!isAutoSave) toast.error(error.response?.data?.msg || "Gagal menyimpan data");
        }
    };

    const handlePassAllConfirm = async (e) => {
        e.preventDefault();
        if (!tempPassAllTeam) return toast.warning("Pilih kontingen terlebih dahulu!");

        setIsPassingAll(true);
        try {
            const res = await api.patch('/weighin/pass-all', { team_id: tempPassAllTeam, type: currentType });
            toast.success(res.data.msg || "Atlet berhasil diloloskan massal!");
            setShowPassAllModal(false);
            fetchAthletes();
        } catch (error) {
            toast.error(error.response?.data?.msg || "Gagal melakukan Pass All.");
        } finally {
            setIsPassingAll(false);
        }
    };

    // --- PDF EXPORT (REVISI: TARIK SEMUA DATA SESUAI FILTER & TANPA GROUPING TIM) ---
    const generatePDF = async () => {
        setIsGeneratingPdf(true);
        try {
            const params = {
                limit: 5000,
                search: searchTerm,
                type: currentType,
                division: filters.division,
                gender: filters.gender,
                team_id: filters.team_id,
                weight_class: filters.weight_class,
                competition_type: 'Kyorugi'
            };

            const response = await api.get('/weighin', { params });
            const allFilteredData = response.data.data || response.data.result || [];

            if (allFilteredData.length === 0) {
                toast.warning("Tidak ada data untuk dicetak pada filter ini!");
                setIsGeneratingPdf(false);
                return;
            }

            allFilteredData.sort((a, b) => {
                let valA = sortBy === 'team' ? (a.team?.name || '') : a.name;
                let valB = sortBy === 'team' ? (b.team?.name || '') : b.name;
                valA = valA.toLowerCase(); valB = valB.toLowerCase();
                if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
                if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
                return 0;
            });

            const doc = new jsPDF('portrait');
            const printDate = new Date().toLocaleString('id-ID');

            let startY = 15;
            let pageNumber = 1;

            const addHeaderPage = (doc, pageNum) => {
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                doc.text(`--- PAGE ${pageNum} ---`, doc.internal.pageSize.getWidth() / 2, 10, { align: 'center' });
            }

            addHeaderPage(doc, pageNumber);

            // HEADER UMUM LAPORAN
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text(`LAPORAN PENIMBANGAN KYORUGI - ${currentType.toUpperCase()}`, 14, startY);

            startY += 6;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');

            // Konfirmasi Filter yang Sedang Aktif ke dalam Header
            let filterTexts = [];
            if (filters.team_id) {
                const tName = teams.find(t => String(t.id) === String(filters.team_id))?.name;
                filterTexts.push(`Kontingen: ${tName || filters.team_id}`);
            } else filterTexts.push(`Kontingen: Semua`);

            if (filters.division) filterTexts.push(`Divisi: ${filters.division}`);
            if (filters.gender) filterTexts.push(`Gender: ${filters.gender === 'Lelaki' ? 'Putra' : 'Putri'}`);
            if (filters.weight_class) filterTexts.push(`Kelas: ${filters.weight_class}`);

            doc.text(`Filter Aktif: ${filterTexts.join(' | ')}`, 14, startY);

            startY += 6;
            doc.text(`Total Peserta: ${allFilteredData.length} Orang`, 14, startY);

            startY += 6;
            doc.text(`Dicetak pada: ${printDate}`, 14, startY);

            startY += 8;

            // DEFINISI KOLOM DINAMIS TABLE TUNGGAL
            let headCols = [];
            let colStyles = {};

            if (isFestival) {
                headCols = [["NO", "KONTINGEN", "NAMA ATLET", "JK", "UMUR", "BERAT (kg)", "TINGGI (cm)", "STATUS"]];
                colStyles = {
                    0: { cellWidth: 10, halign: 'center' },
                    1: { cellWidth: 35, halign: 'left' },
                    2: { cellWidth: 40, halign: 'left' },
                    3: { cellWidth: 10, halign: 'center' },
                    4: { cellWidth: 15, halign: 'center' },
                    5: { cellWidth: 22, halign: 'center' },
                    6: { cellWidth: 22, halign: 'center' },
                    7: { cellWidth: 20, halign: 'center' },
                };
            } else {
                headCols = [["NO", "KONTINGEN", "NAMA ATLET", "JK", "DIVISI", "KELAS", "BERAT (kg)", "STATUS"]];
                colStyles = {
                    0: { cellWidth: 10, halign: 'center' },
                    1: { cellWidth: 35, halign: 'left' },
                    2: { cellWidth: 45, halign: 'left' },
                    3: { cellWidth: 10, halign: 'center' },
                    4: { cellWidth: 18, halign: 'center' },
                    5: { cellWidth: 25, halign: 'center' },
                    6: { cellWidth: 20, halign: 'center' },
                    7: { cellWidth: 18, halign: 'center' },
                };
            }

            const bodyRows = allFilteredData.map((a, i) => {
                const actualWeightVal = inputData[a.uuid || a.id]?.actual_weight || a.actual_weight || '-';
                const actualHeightVal = inputData[a.uuid || a.id]?.height || a.height || '-';
                const teamName = a.team?.name || 'UNATTACHED';
                const genderStr = a.gender === 'Lelaki' || a.gender === 'L' ? 'L' : 'P';

                let ageText = "-";
                if (a.age) {
                    ageText = `${a.age} Thn`;
                } else if (a.birth_year) {
                    const calculatedAge = eventYear - a.birth_year;
                    ageText = calculatedAge > 0 ? `${calculatedAge} Thn` : "-";
                }

                const finalStatus = evaluateWeightStatus(a, actualWeightVal);
                const statusLabel = finalStatus === 'Pass' ? 'Lolos' : finalStatus === 'Fail' ? 'Gagal' : 'Pending';

                if (isFestival) {
                    return [
                        i + 1, teamName.toUpperCase(), a.name.toUpperCase(), genderStr,
                        ageText, actualWeightVal, actualHeightVal, statusLabel
                    ];
                } else {
                    return [
                        i + 1, teamName.toUpperCase(), a.name.toUpperCase(), genderStr,
                        a.division, a.weight_class || '-', actualWeightVal, statusLabel
                    ];
                }
            });

            // RENDER TABEL TUNGGAL
            autoTable(doc, {
                startY: startY,
                head: headCols,
                body: bodyRows,
                theme: 'grid',
                styles: {
                    font: 'helvetica',
                    fontSize: 8,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1
                },
                headStyles: {
                    fillColor: [230, 230, 230],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    halign: 'center'
                },
                columnStyles: colStyles,
            });

            // Tambah Footer Halaman
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(10);
                doc.text(`Halaman ${i} - Laporan Penimbangan ${currentType}`, 14, doc.internal.pageSize.getHeight() - 10);
            }

            doc.save(`Laporan_Penimbangan_${currentType}_${new Date().toISOString().split('T')[0]}.pdf`);

        } catch (error) {
            toast.error("Gagal menarik data untuk cetak PDF.");
            console.error("PDF Generate Error: ", error);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handlePrevPage = () => { if (page > 1) setPage(page - 1); };
    const handleNextPage = () => { if (page < totalPages) setPage(page + 1); };

    return (
        <div className="container-fluid p-0 animate-fade-in">
            {/* HEADER SECTION */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                        <FaWeight className="text-primary" /> Penimbangan Kyorugi {currentType}
                    </h4>
                    <p className="text-muted m-0 small">Ketik berat, lalu tekan <b>TAB</b>. Sistem akan mengevaluasi & menyimpan otomatis.</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    {!isFestival && (
                        <button onClick={() => setShowToleranceModal(true)} className="btn btn-white border shadow-sm rounded-pill px-3 fw-bold text-dark hover-scale d-flex align-items-center gap-2">
                            <FaSlidersH className="text-primary" /> Toleransi: {tolerance} Kg
                        </button>
                    )}
                    <button onClick={() => setShowPassAllModal(true)} className="btn btn-success text-white border border-success border-opacity-50 shadow-sm rounded-pill px-3 fw-bold hover-scale d-flex align-items-center gap-2">
                        <FaCheckCircle /> Pass All By Team
                    </button>
                    <button onClick={generatePDF} disabled={isGeneratingPdf} className="btn btn-danger shadow-sm rounded-pill px-4 fw-bold hover-scale d-flex align-items-center gap-2">
                        {isGeneratingPdf ? <><div className="spinner-border spinner-border-sm"></div> Menyiapkan...</> : <><BsFileEarmarkPdf /> Cetak PDF</>}
                    </button>
                </div>
            </div>

            {/* FILTER SECTION */}
            <div className="card border-0 shadow-sm mb-4 rounded-4 bg-white">
                <div className="card-body p-4">
                    <div className="row g-2">
                        <div className="col-md-3">
                            <div className="input-group shadow-sm rounded-pill overflow-hidden h-100">
                                <span className="input-group-text bg-light border-0 ps-4 text-muted"><BsSearch /></span>
                                <input type="text" className="form-control bg-light border-0 py-2 px-2" placeholder="Cari Nama..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select className="form-select bg-light border-0 shadow-sm rounded-pill py-2 text-muted fw-medium px-4 h-100" value={filters.team_id} onChange={(e) => setFilters({ ...filters, team_id: e.target.value })}>
                                <option value="">Semua Kontingen</option>
                                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                        {!isFestival && (
                            <div className="col-md-2">
                                <select
                                    className="form-select bg-light border-0 shadow-sm rounded-pill py-2 text-muted fw-medium px-4 h-100"
                                    value={filters.division}
                                    onChange={(e) => setFilters({ ...filters, division: e.target.value, weight_class: '' })}
                                >
                                    <option value="">Semua Divisi</option>
                                    {masterData?.divisions?.map((div, idx) => (
                                        <option key={idx} value={div.name}>{div.name}</option>
                                    ))}
                                    {(!masterData?.divisions || masterData.divisions.length === 0) && (
                                        <>
                                            <option value="Cadet">Cadet</option>
                                            <option value="Junior">Junior</option>
                                            <option value="Senior">Senior</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        )}
                        {!isFestival && (
                            <>
                                <div className="col-md-2">
                                    <select className="form-select bg-light border-0 shadow-sm rounded-pill py-2 text-muted fw-medium px-4 h-100" value={filters.gender} onChange={(e) => setFilters({ ...filters, gender: e.target.value, weight_class: '' })}>
                                        <option value="">Gender</option>
                                        <option value="Lelaki">Putra</option>
                                        <option value="Perempuan">Putri</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    {availableClasses && availableClasses.length > 0 ? (
                                        <select
                                            className="form-select bg-light border-0 shadow-sm rounded-pill py-2 text-muted fw-medium px-4 h-100"
                                            value={filters.weight_class}
                                            onChange={(e) => setFilters({ ...filters, weight_class: e.target.value })}
                                        >
                                            <option value="">Semua Kelas</option>
                                            {availableClasses.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0 shadow-sm rounded-pill py-2 text-muted h-100 px-4"
                                            placeholder="Filter Kelas"
                                            value={filters.weight_class}
                                            onChange={(e) => setFilters({ ...filters, weight_class: e.target.value })}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* DATA TABLE CARD */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4" style={{ minHeight: '400px' }}>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                            <span className="text-muted fw-bold">Memuat data penimbangan...</span>
                        </div>
                    ) : sortedAthletes.length === 0 ? (
                        <div className="text-center py-5">
                            <BsSpeedometer2 size={60} className="text-muted opacity-25 mb-3" />
                            <h5 className="text-muted fw-bold">Tidak ada atlet ditemukan.</h5>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 text-nowrap" style={{ minWidth: '900px' }}>
                                <thead className="table-light">
                                    <tr>
                                        <th className="py-3 ps-4 text-muted small text-uppercase fw-bold border-0 cursor-pointer" onClick={() => toggleSort('team')} style={{ letterSpacing: '0.5px' }}>
                                            Tim & Atlet {sortBy === 'team' && (sortOrder === 'asc' ? <BsSortAlphaDown /> : <BsSortAlphaUp />)}
                                        </th>

                                        {!isFestival && (
                                            <th className="py-3 text-muted small text-uppercase fw-bold border-0" style={{ letterSpacing: '0.5px' }}>Divisi</th>
                                        )}
                                        <th className="py-3 text-center text-muted small text-uppercase fw-bold border-0">Umur</th>

                                        {!isFestival && <th className="py-3 text-muted small text-uppercase fw-bold border-0">Kelas</th>}
                                        {isFestival && <th className="py-3 text-muted small text-uppercase fw-bold border-0 text-center" style={{ width: '120px' }}>Tinggi (Cm)</th>}

                                        <th className="py-3 text-muted small text-uppercase fw-bold border-0 text-center" style={{ width: '120px' }}>Berat (Kg)</th>
                                        <th className="py-3 text-muted small text-uppercase fw-bold border-0 text-center" style={{ letterSpacing: '0.5px' }}>Status</th>
                                        <th className="py-3 pe-4 text-muted small text-uppercase fw-bold border-0 text-end" style={{ letterSpacing: '0.5px' }}>Aksi</th>
                                    </tr>
                                </thead>
                                <tbody className="border-top-0">
                                    {sortedAthletes.map((athlete) => {
                                        const aid = athlete.uuid || athlete.id;

                                        const actualWeightVal = inputData[aid]?.actual_weight !== undefined ? inputData[aid].actual_weight : athlete.actual_weight || '';
                                        const actualHeightVal = inputData[aid]?.height !== undefined ? inputData[aid].height : athlete.height || '';

                                        const liveStatus = evaluateWeightStatus(athlete, actualWeightVal);
                                        const { min, max } = parseWeightClass(athlete.weight_class);

                                        let ageText = "-";
                                        if (athlete.age) {
                                            ageText = `${athlete.age} Thn`;
                                        } else if (athlete.birth_year) {
                                            const calculatedAge = eventYear - athlete.birth_year;
                                            ageText = calculatedAge > 0 ? `${calculatedAge} Thn` : "-";
                                        }

                                        return (
                                            <tr key={aid} className="hover-up-row">
                                                <td className="ps-4 py-3 border-bottom-0">
                                                    <div className="d-flex align-items-center gap-3">
                                                        {!isFestival && athlete.image ? (
                                                            <img crossOrigin="anonymous" src={`${process.env.REACT_APP_BACKEND}/api/uploads/proxy?file=uploads/athletes/${athlete.image}`} alt="" className="rounded-circle border" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                                        ) : (
                                                            <div className="rounded-circle bg-light d-flex justify-content-center align-items-center text-muted border" style={{ width: '40px', height: '40px' }}><FaUserNinja /></div>
                                                        )}
                                                        <div>
                                                            <div className="fw-black text-dark text-uppercase">{athlete.name}</div>
                                                            <small className="badge bg-light text-dark border mt-1">{athlete.team?.name || '-'}</small>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* DIVISI */}
                                                {!isFestival && (
                                                    <td className="border-bottom-0">
                                                        <span className="fw-bold text-primary d-block">{athlete.division}</span>
                                                    </td>
                                                )}

                                                {/* UMUR */}
                                                <td className="border-bottom-0 text-center">
                                                    <span className="fw-bold text-dark">{ageText}</span>
                                                </td>

                                                {/* KELAS KHUSUS PRESTASI */}
                                                {!isFestival && (
                                                    <td className="border-bottom-0">
                                                        <span className="badge bg-secondary bg-opacity-10 text-secondary border px-2 py-1">
                                                            {athlete.weight_class}
                                                        </span>
                                                    </td>
                                                )}

                                                {/* TINGGI KHUSUS FESTIVAL */}
                                                {isFestival && (
                                                    <td className="border-bottom-0">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center bg-light border-0 shadow-sm rounded-pill fw-bold"
                                                            style={{ height: '38px', fontSize: '1.1rem' }}
                                                            placeholder={athlete.height ? `(${athlete.height})` : "0"}
                                                            value={actualHeightVal}
                                                            onChange={(e) => handleInputChange(aid, 'height', e.target.value)}
                                                            onBlur={() => handleSaveWeight(athlete, true)}
                                                        />
                                                    </td>
                                                )}

                                                {/* BERAT */}
                                                <td className="border-bottom-0 text-center">
                                                    <input
                                                        type="number" step="0.1"
                                                        className="form-control form-control-sm text-center bg-light border-0 shadow-sm rounded-pill fw-bold mx-auto"
                                                        style={{ height: '38px', fontSize: '1.1rem', maxWidth: '100px' }}
                                                        placeholder={athlete.declared_weight ? `(${athlete.declared_weight})` : "0.0"}
                                                        value={actualWeightVal}
                                                        onChange={(e) => handleInputChange(aid, 'actual_weight', e.target.value)}
                                                        onBlur={() => handleSaveWeight(athlete, true)}
                                                    />
                                                    {!isFestival && (
                                                        <div className="small opacity-50 mt-1" style={{ fontSize: '0.65rem' }}>
                                                            Toleransi: {Math.max(0, min - tolerance).toFixed(1)} - {(max + tolerance).toFixed(1)}
                                                        </div>
                                                    )}
                                                </td>

                                                {/* STATUS */}
                                                <td className="text-center border-bottom-0">
                                                    <div className={`badge rounded-pill px-3 py-2 shadow-sm transition-all ${liveStatus === 'Pass' ? 'bg-success bg-opacity-25 text-success border border-success border-opacity-50' :
                                                        liveStatus === 'Fail' ? 'bg-danger bg-opacity-25 text-danger border border-danger border-opacity-50' :
                                                            'bg-warning bg-opacity-25 text-warning border border-warning border-opacity-50'
                                                        }`}>
                                                        {liveStatus === 'Pass' ? 'Lolos' : liveStatus === 'Fail' ? 'Gagal' : 'Pending'}
                                                    </div>
                                                </td>

                                                <td className="pe-4 text-end border-bottom-0">
                                                    <button
                                                        onClick={() => handleSaveWeight(athlete, false)}
                                                        className="btn btn-light bg-white border text-primary rounded-circle hover-scale shadow-sm d-flex justify-content-center align-items-center p-0 ms-auto"
                                                        style={{ width: '35px', height: '35px' }} title="Simpan Manual"
                                                    >
                                                        <BsSave />
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* PAGINATION */}
                <div className="card-footer bg-white border-top p-4 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                        <select className="form-select form-select-sm shadow-sm rounded-pill fw-bold" style={{ width: '100px' }} value={limit} onChange={(e) => { setLimit(e.target.value); setPage(1); }}>
                            <option value="20">20 Baris</option>
                            <option value="50">50 Baris</option>
                            <option value="100">100 Baris</option>
                        </select>
                        <span className="text-muted small fw-bold d-none d-md-inline">Halaman {page} dari {totalPages || 1}</span>
                    </div>
                    <nav>
                        <ul className="pagination pagination-sm mb-0 gap-1 shadow-sm rounded">
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <button className="page-link border-0 text-dark fw-bold" onClick={handlePrevPage}>Prev</button>
                            </li>
                            <li className="page-item active">
                                <button className="page-link border-0 bg-primary fw-bold px-3">{page}</button>
                            </li>
                            <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                                <button className="page-link border-0 text-dark fw-bold" onClick={handleNextPage}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            {/* --- MODAL TOLERANSI --- */}
            {showToleranceModal && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1060, backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)' }}>
                    <div className="card border-0 shadow-lg rounded-4 p-0 overflow-hidden animate-pop-in" style={{ width: '90%', maxWidth: '400px' }}>
                        <div className="card-header bg-white p-4 border-bottom-0 d-flex justify-content-between align-items-center">
                            <h5 className="fw-bold m-0 text-dark d-flex align-items-center gap-2"><FaSlidersH className="text-primary" /> Pengaturan Toleransi</h5>
                            <button onClick={() => setShowToleranceModal(false)} className="btn btn-sm btn-light rounded-circle hover-scale text-danger d-flex align-items-center justify-content-center p-0" style={{ width: '32px', height: '32px' }}><FaTimes /></button>
                        </div>
                        <div className="card-body px-4 pb-4 pt-2">
                            <label className="form-label small fw-bold text-muted">Batas Toleransi Timbang (Kg)</label>
                            <input
                                type="number"
                                step="0.1"
                                className="form-control bg-light border-0 py-3 rounded-pill text-center fw-bold fs-5 shadow-sm mb-4"
                                value={tempTolerance}
                                onChange={(e) => setTempTolerance(e.target.value)}
                            />
                            <button
                                onClick={() => { setTolerance(parseFloat(tempTolerance) || 0); setShowToleranceModal(false); toast.success("Toleransi diperbarui"); }}
                                className="btn btn-primary w-100 rounded-pill py-2 fw-bold shadow-sm hover-scale d-flex justify-content-center align-items-center gap-2"
                            >
                                <BsCheckLg /> Simpan Pengaturan
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* --- MODAL PASS ALL --- */}
            {showPassAllModal && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1060, backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)' }}>
                    <div className="card border-0 shadow-lg rounded-4 p-0 overflow-hidden animate-pop-in" style={{ maxWidth: '450px', width: '90%' }}>
                        <div className="card-header bg-success bg-opacity-10 p-4 border-0 d-flex justify-content-between align-items-center">
                            <h5 className="fw-bold m-0 text-success d-flex align-items-center gap-2"><BsCheckAll size={24} /> Loloskan Tim Massal</h5>
                            <button onClick={() => setShowPassAllModal(false)} className="btn btn-sm btn-link text-muted p-0"><FaTimes size={18} /></button>
                        </div>
                        <form onSubmit={handlePassAllConfirm}>
                            <div className="card-body px-4 pt-4 pb-3">
                                <div className="alert alert-warning bg-warning bg-opacity-10 border-0 shadow-sm rounded-4 small d-flex gap-2 mb-4">
                                    <BsExclamationTriangleFill className="mt-1 flex-shrink-0 text-warning" />
                                    <span className="text-dark">Tindakan ini akan otomatis meloloskan atlet berstatus <b>Pending</b> pada kategori Kyorugi {currentType}.</span>
                                </div>
                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-muted">Pilih Kontingen</label>
                                    <select
                                        className="form-select bg-light border-0 py-3 shadow-sm rounded-pill fw-medium px-4"
                                        required
                                        value={tempPassAllTeam}
                                        onChange={(e) => setTempPassAllTeam(e.target.value)}
                                    >
                                        <option value="">-- Silakan Pilih Tim --</option>
                                        <option value="all" className="fw-bold text-primary">✓ Loloskan SEMUA TIM Sekaligus</option>
                                        <option disabled>──────────────────────</option>
                                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                </div>
                                <div className="d-flex justify-content-end gap-2">
                                    <button type="button" onClick={() => setShowPassAllModal(false)} className="btn btn-light fw-bold rounded-pill px-4 hover-scale border">Batal</button>
                                    <button type="submit" className="btn btn-success fw-bold rounded-pill px-4 shadow-sm hover-scale d-flex align-items-center gap-2" disabled={isPassingAll}>
                                        {isPassingAll ? <><div className="spinner-border spinner-border-sm"></div> Memproses...</> : <><BsCheckLg /> Eksekusi Pass All</>}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* CSS STYLE */}
            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .animate-pop-in { animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.05); }
                .hover-up-row { transition: background-color 0.2s; }
                .hover-up-row:hover { background-color: #f8f9fa !important; }
                .cursor-pointer { cursor: pointer; }
                .cursor-pointer:hover { background-color: #e9ecef; transition: 0.2s; }
                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes popIn { 
                    0% { transform: scale(0.9); opacity: 0; } 
                    100% { transform: scale(1); opacity: 1; } 
                }
            `}</style>
        </div>
    );
};

export default AdminWeighIn;